import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import QRCode from 'qrcode'
import { Loader2, Save, Shield, FlaskConical, FileDown } from 'lucide-react'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { FiscalTestConnectionResult, FiscalDiSn002SandboxValidateResult } from '@/types'
import { FiscalInvoiceDocument } from '@/components/fiscal/FiscalInvoiceDocument'
import { parseReceiptSettings } from '@/lib/printCustomerReceipt'
import { downloadSn002TestReceipt } from '@/lib/sn002TestReceipt'

/** Renders QR with the same `qrcode` dependency used elsewhere (avoids fragile `qrcode.react` installs in Docker). */
function FiscalTestQrImg({ value, size }: { value: string; size: number }) {
  const [src, setSrc] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    QRCode.toDataURL(value, { width: size, margin: 2 })
      .then((url) => {
        if (!cancelled) setSrc(url)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [value, size])

  if (!src) {
    return (
      <Loader2
        className="animate-spin text-muted-foreground"
        style={{ width: size, height: size }}
        aria-hidden
      />
    )
  }
  return <img src={src} alt="Fiscal test QR" width={size} height={size} className="max-w-full rounded-sm" />
}

/** Backend may return real PRAL JSON or `{"_preview_error":"..."}` when preview build failed. */
function parsePralPreviewBuildError(raw: string): string | null {
  const s = raw.trim()
  if (!s.startsWith('{')) return null
  try {
    const o = JSON.parse(s) as { _preview_error?: string }
    return typeof o._preview_error === 'string' ? o._preview_error : null
  } catch {
    return null
  }
}

function getFiscalTestRequestPreview(r: FiscalTestConnectionResult | null): string {
  if (!r) return ''
  const alt = r as FiscalTestConnectionResult & { requestPreview?: string }
  return (r.request_preview ?? alt.requestPreview ?? '').trim()
}

export function FiscalSettingsPanel() {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  // Both queries below MUST cache the raw APIResponse envelope, not the
  // unwrapped payload. These keys are shared app-wide (['settings','all'] has
  // ~30 consumers via useBusinessName/useDayBoundaryHour/CurrencyContext/…;
  // ['fiscal','config'] is also read by useFiscalStatus) and every other
  // consumer reads `.data` off the envelope. Unwrapping here poisoned the
  // shared cache: whichever query resolved last decided the shape, so e.g.
  // receipt-settings parsing or fiscal-authority detection silently broke on a
  // background refetch. Same bug class as F-FE-N6. Read `.data` locally instead.
  const { data: settingsRes } = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })
  const settingsRaw = (settingsRes?.data ?? {}) as Record<string, unknown>
  const receiptBranding = parseReceiptSettings(settingsRaw)
  const { data: fiscalRes, isLoading } = useQuery({
    queryKey: ['fiscal', 'config'],
    queryFn: () => apiClient.getFiscalConfig(),
  })
  const data = fiscalRes?.data

  const [authority, setAuthority] = useState('NONE')
  const [posId, setPosId] = useState('')
  const [ntn, setNtn] = useState('')
  const [isSandbox, setIsSandbox] = useState(true)
  const [strn, setStrn] = useState('')
  const [pntn, setPntn] = useState('')
  const [posReg, setPosReg] = useState('')
  const [sfdUrl, setSfdUrl] = useState('http://localhost:16701')
  const [sellerBusinessName, setSellerBusinessName] = useState('')
  const [sellerProvince, setSellerProvince] = useState('')
  const [sellerAddress, setSellerAddress] = useState('')
  const [buyerRegDefault, setBuyerRegDefault] = useState('Unregistered')
  const [posPostUrl, setPosPostUrl] = useState('')
  const [fbrValidateURL, setFbrValidateURL] = useState('')
  const [fbrPostURL, setFbrPostURL] = useState('')
  const [apiKey, setApiKey] = useState('')

  useEffect(() => {
    if (!data) return
    setAuthority(data.authority || 'NONE')
    setPosId(data.pos_id || '')
    setNtn(data.ntn || '')
    setIsSandbox(data.is_sandbox !== false)
    setStrn(data.strn || '')
    setPntn(data.pntn || '')
    setPosReg(data.pos_registration_number || '')
    setSfdUrl(data.sfd_proxy_url || 'http://localhost:16701')
    setSellerBusinessName(data.seller_business_name || '')
    setSellerProvince(data.seller_province || '')
    setSellerAddress(data.seller_address || '')
    setBuyerRegDefault(data.buyer_registration_default || 'Unregistered')
    setPosPostUrl(data.pos_post_url || '')
    setFbrValidateURL(data.fbr_validate_url || '')
    setFbrPostURL(data.fbr_post_url || '')
    setApiKey('')
  }, [data])

  const authorityNeedsToken =
    authority === 'LOCAL_BRIDGE' ||
    authority === 'PRAL_POS' ||
    authority === 'FBR' ||
    authority === 'PRA' ||
    authority === 'PRA_EIMS'
  // PRA e-IMS needs POS ID + Bearer token in BOTH modes, not just production:
  // the sandbox token is per-environment and the POS ID is a distinct test POS
  // minted from PRA's portal, so neither can be inferred.
  const praEims = authority === 'PRA_EIMS'
  const productionTokenMissing =
    ((authority === 'LOCAL_BRIDGE' || authority === 'PRAL_POS') && !isSandbox && !data?.api_key_set && !apiKey.trim()) ||
    (praEims && !data?.api_key_set && !apiKey.trim())
  const productionPosIdMissing =
    ((authority === 'LOCAL_BRIDGE' || authority === 'PRAL_POS') && !isSandbox && !posId.trim()) ||
    (praEims && !posId.trim())

  const saveMut = useMutation({
    mutationFn: () => {
      const authUp = authority.trim().toUpperCase()
      if (productionTokenMissing) {
        return Promise.reject(
          new Error(
            'Production mode requires a Bearer token. Paste the FBR-issued live token, or switch Sandbox back ON.',
          ),
        )
      }
      if (productionPosIdMissing) {
        return Promise.reject(new Error('Production mode requires a POS ID (digits only).'))
      }
      return apiClient.putFiscalConfig({
        authority: authUp,
        pos_id: posId,
        ntn,
        is_sandbox: isSandbox,
        strn: authUp === 'FBR' ? strn.trim() : undefined,
        pntn: authUp === 'PRA' ? pntn : undefined,
        pos_registration_number: authUp === 'PRA' ? posReg : undefined,
        sfd_proxy_url: authUp === 'PRA' ? sfdUrl : undefined,
        pos_post_url: authUp === 'PRAL_POS' ? posPostUrl.trim() || undefined : undefined,
        fbr_validate_url:
          authUp === 'FBR' && fbrValidateURL.trim() ? fbrValidateURL.trim() : undefined,
        fbr_post_url: authUp === 'FBR' && fbrPostURL.trim() ? fbrPostURL.trim() : undefined,
        seller_business_name: authUp === 'FBR' ? sellerBusinessName : undefined,
        seller_province: authUp === 'FBR' ? sellerProvince : undefined,
        seller_address: authUp === 'FBR' ? sellerAddress : undefined,
        buyer_registration_default: authUp === 'FBR' ? buyerRegDefault : undefined,
        ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fiscal', 'config'] })
      toast({ title: 'Saved', description: 'Fiscal configuration updated.' })
    },
    onError: (e: Error) => toast({ title: 'Save failed', description: e.message, variant: 'destructive' }),
  })

  const [testOpen, setTestOpen] = useState(false)
  const [testResult, setTestResult] = useState<FiscalTestConnectionResult | null>(null)
  const testMut = useMutation({
    mutationFn: () => apiClient.postFiscalTestConnection({ authority: authority || undefined }),
    onSuccess: async (res) => {
      if (!res.data) return
      let result: FiscalTestConnectionResult = res.data

      // LOCAL_BRIDGE sandbox=ON: backend returns payload preview only; browser must relay to localhost:8524.
      // LOCAL_BRIDGE sandbox=OFF: backend already called gw.fbr.gov.pk and returned a real result — skip.
      if (result.authority === 'LOCAL_BRIDGE' && result.is_sandbox && result.request_preview) {
        try {
          const localResp = await fetch(
            'http://localhost:8524/api/IMSFiscal/GetInvoiceNumberByModel',
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: result.request_preview,
            },
          )
          const localJson = await localResp.text()
          let irn = ''
          let qr = ''
          let errorMsg = ''
          try {
            const parsed = JSON.parse(localJson) as Record<string, unknown>
            const code = String(parsed.Code ?? '')
            if (code === '100' || code === '') {
              irn = String(parsed.InvoiceNumber ?? '')
              qr = String(
                (parsed.QRCode as string | undefined) ??
                  (parsed.QrCode as string | undefined) ??
                  '',
              )
              if (!irn || irn.toLowerCase() === 'not available') {
                irn = ''
                errorMsg =
                  String(parsed.Response ?? parsed.Errors ?? '') ||
                  'No invoice number returned'
              }
            } else {
              errorMsg = `Code ${code}: ${String(parsed.Response ?? parsed.Errors ?? 'Rejected')}`
            }
          } catch {
            errorMsg = 'Invalid JSON from IMSFiscal'
          }
          result = {
            ...result,
            ok: !!irn,
            irn,
            qr_code_value: qr || irn,
            raw: localJson,
            error: errorMsg || undefined,
          }
        } catch (fetchErr: unknown) {
          const msg = fetchErr instanceof Error ? fetchErr.message : String(fetchErr)
          const errMsg =
            msg.includes('Failed to fetch') ||
            msg.includes('NetworkError') ||
            msg.includes('CORS') ||
            msg.includes('Load failed')
              ? 'IMSFiscal not running on this machine — start the FBR fiscal desktop software on this counter and try again.'
              : msg || 'Local fiscal relay failed'
          result = { ...result, ok: false, error: errMsg }
        }
      }

      setTestResult(result)
      setTestOpen(true)
      if (result.error) {
        toast({ title: 'Test connection failed', description: result.error, variant: 'destructive' })
      } else if (result.ok) {
        toast({
          title: 'Test passed',
          description: result.irn
            ? `Invoice registered: ${result.irn}. The same IRN and QR appear on checkout receipts.`
            : 'Gateway responded OK.',
        })
      } else {
        toast({
          title: 'Test incomplete',
          description:
            (result.raw && result.raw.slice(0, 280)) ||
            'No IRN — open the dialog for the raw response or fix PCT / totals.',
          variant: 'destructive',
        })
      }
    },
    onError: (e: Error) => toast({ title: 'Test failed', description: e.message, variant: 'destructive' }),
  })

  const pralOutboundPreview = getFiscalTestRequestPreview(testResult)
  const pralPreviewBuildErr =
    testResult?.authority === 'PRAL_POS' || testResult?.authority === 'LOCAL_BRIDGE' ? parsePralPreviewBuildError(pralOutboundPreview) : null

  const [sn002DialogOpen, setSn002DialogOpen] = useState(false)
  const [sn002Result, setSn002Result] = useState<FiscalDiSn002SandboxValidateResult | null>(null)
  const [sn002ReceiptBusy, setSn002ReceiptBusy] = useState(false)

  const sn002Mut = useMutation({
    mutationFn: () => apiClient.postFiscalDiSn002SandboxValidate(),
    onSuccess: (res) => {
      if (res.success && res.data) {
        setSn002Result(res.data)
        if (!res.data.ok) {
          toast({
            title: 'FBR sandbox validate (SN002)',
            description:
              res.data.error ||
              (res.data.http_status >= 400
                ? `HTTP ${res.data.http_status}`
                : 'Response did not indicate success'),
            variant: 'destructive',
          })
        }
      }
    },
    onError: (e: Error) =>
      toast({ title: 'SN002 validate failed', description: e.message, variant: 'destructive' }),
  })

  const openSn002Test = () => {
    setSn002Result(null)
    setSn002DialogOpen(true)
    sn002Mut.mutate()
  }

  if (isLoading && !data) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading fiscal settings…
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" />
            Authority & credentials
          </CardTitle>
          <CardDescription>Set up by your bhookly team. Changes apply after save.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Authority</Label>
            <Select value={authority} onValueChange={setAuthority}>
              <SelectTrigger>
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">None (mock only)</SelectItem>
                <SelectItem value="PRAL_POS">PRAL POS — POST invoice (POS ID)</SelectItem>
                <SelectItem value="LOCAL_BRIDGE">Local Bridge — IMSFiscal on localhost (POS ID)</SelectItem>
                <SelectItem value="FBR">FBR Digital Invoicing — gw.fbr.gov.pk (alternate)</SelectItem>
                <SelectItem value="PRA_EIMS">PRA e-IMS — Punjab Revenue Authority (POS ID + token)</SelectItem>
                <SelectItem value="PRA">PRA Punjab SFD (legacy — do not use)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-3 rounded-md border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Sandbox mode (automatic sync)</p>
                <p className="text-xs text-muted-foreground">
                  Keep on while testing. Switch off only for live government submissions.
                </p>
              </div>
              <Switch checked={isSandbox} onCheckedChange={setIsSandbox} />
            </div>

            {authority === 'FBR' && isSandbox && (
              <div className="border-t pt-3 space-y-2">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      DI sandbox checklist (scenario SN002)
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 max-w-prose">
                      Posts the SN002 template to your sandbox DI URL (<span className="font-mono">postinvoicedata_sb</span> by
                      default — same gateway path as outbound post — or your checklist URL override below) with Bearer from this
                      page. Seller <span className="font-semibold">STRN</span> (→{' '}
                      <span className="font-mono">sellerNTNCNIC</span>), business name, and address come from saved settings;
                      buyer and line totals are the fixed unregistered bakery template.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 gap-2"
                    disabled={!data?.api_key_set || sn002Mut.isPending}
                    onClick={openSn002Test}
                  >
                    {sn002Mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                    Validate SN002 invoice
                  </Button>
                </div>
                {!data?.api_key_set && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">
                    Save fiscal settings with a sandbox Bearer token before running this checklist.
                  </p>
                )}
              </div>
            )}
          </div>

          {authority === 'FBR' && (
            <div className="space-y-2">
              <Label>STRN (sales tax registration — sent as sellerNTNCNIC)</Label>
              <Input value={strn} onChange={(e) => setStrn(e.target.value)} placeholder="Required for FBR DI; digits as paired with Bearer token" />
              <p className="text-xs text-muted-foreground">
                Saved STRN is formatted and placed in{' '}
                <span className="font-mono">sellerNTNCNIC</span> on every Digital Invoicing request (sandbox checklist and checkout
                sync). NTN below is kept for records only and is{' '}
                <span className="font-medium">not</span> substituted for DI.
              </p>
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>
                NTN / Tax ID
                {authority === 'FBR' ? ' (not used for sellerNTNCNIC)' : ''}
                {praEims ? ' (bookkeeping only)' : ''}
              </Label>
              <Input value={ntn} onChange={(e) => setNtn(e.target.value)} placeholder="1234567-8" />
              {authority === 'FBR' && (
                <p className="text-xs text-muted-foreground">Optional bookkeeping; Chaikhana does not send NTN as the DI seller registration.</p>
              )}
              {praEims && (
                <p className="text-xs text-muted-foreground">
                  Not sent to PRA. The seller is identified by POS ID alone.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label>
                {praEims
                  ? 'POS ID (required)'
                  : authority === 'PRA' || authority === 'PRAL_POS' || authority === 'LOCAL_BRIDGE'
                    ? 'POS ID (digits)'
                    : 'POS ID (optional)'}
              </Label>
              <Input
                value={posId}
                onChange={(e) => setPosId(e.target.value)}
                placeholder={
                  authority === 'PRAL_POS' || authority === 'LOCAL_BRIDGE'
                    ? 'PRAL-assigned POS ID integer'
                    : authority === 'FBR'
                      ? 'Not used for DI'
                      : 'Terminal ID'
                }
              />
            </div>
          </div>

          {authority === 'PRAL_POS' && (
            <div className="rounded-md border border-muted bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              This path matches PRAL's POS integrator contract (PascalCase JSON,{' '}
              <span className="font-mono">POSID</span>, Bearer). It is not FBR's standalone Digital Invoicing web workflow — both can
              stay in the product so other deployments can select DI when needed.
            </div>
          )}

          {authority === 'LOCAL_BRIDGE' && (
            <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
              <strong>Local Bridge</strong> builds the same PRAL POS payload but does not POST from the server.
              Instead, the browser at the counter machine relays the invoice to{' '}
              <code className="text-[10px]">http://localhost:8524/api/IMSFiscal/GetInvoiceNumberByModel</code>{' '}
              (the FBR desktop fiscal server). Requires IMSFiscal running on the POS machine. Reports and
              remote access work normally — only fiscalization runs locally.
            </div>
          )}

          {authority === 'PRAL_POS' && (
            <div className="space-y-2">
              <Label>POS POST URL (PRAL)</Label>
              <div className="flex gap-2">
                <Input
                  value={posPostUrl}
                  onChange={(e) => setPosPostUrl(e.target.value)}
                  placeholder={
                    isSandbox
                      ? 'Leave blank → esp.fbr.gov.pk:8244 (PRAL sandbox default)'
                      : 'Leave blank → gw.fbr.gov.pk/imsp/v1/api/Live/PostData (production default)'
                  }
                  className="flex-1"
                />
                {!posPostUrl.trim() && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="shrink-0 text-xs"
                    onClick={() =>
                      setPosPostUrl(
                        isSandbox
                          ? 'https://esp.fbr.gov.pk:8244/FBR/v1/api/Live/PostData'
                          : 'https://gw.fbr.gov.pk/imsp/v1/api/Live/PostData',
                      )
                    }
                  >
                    Fill default
                  </Button>
                )}
              </div>
              <div className="rounded-md border bg-muted/40 px-3 py-2 space-y-1">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Effective POST URL {isSandbox ? '(sandbox)' : '(production)'}
                </p>
                <p className="text-xs font-mono break-all text-foreground">
                  {posPostUrl.trim() ||
                    (isSandbox
                      ? 'https://esp.fbr.gov.pk:8244/FBR/v1/api/Live/PostData'
                      : 'https://gw.fbr.gov.pk/imsp/v1/api/Live/PostData')}
                </p>
                {!isSandbox && !posPostUrl.trim() && (
                  <p className="text-[11px] text-amber-700 dark:text-amber-400">
                    Production mode — using FBR IMSP gateway (gw.fbr.gov.pk, port 443). Set POS ID and Bearer token to match your FBR registration.
                  </p>
                )}
                {isSandbox && !posPostUrl.trim() && (
                  <p className="text-[11px] text-muted-foreground">
                    Sandbox mode — using PRAL ESP gateway (port 8244). If Railway times out on this port, switch sandbox off to use the production gateway (port 443).
                  </p>
                )}
              </div>
            </div>
          )}

          {authority === 'FBR' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Seller business name (FBR invoice)</Label>
                <Input
                  value={sellerBusinessName}
                  onChange={(e) => setSellerBusinessName(e.target.value)}
                  placeholder="Falls back to General → Restaurant name if empty"
                />
              </div>
              <div className="space-y-2">
                <Label>Seller province</Label>
                <Input value={sellerProvince} onChange={(e) => setSellerProvince(e.target.value)} placeholder="e.g. Punjab" />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Seller address</Label>
                <Input value={sellerAddress} onChange={(e) => setSellerAddress(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Default buyer registration type (B2C walk-in)</Label>
                <Select value={buyerRegDefault} onValueChange={setBuyerRegDefault}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Unregistered">Unregistered</SelectItem>
                    <SelectItem value="Registered">Registered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-muted-foreground">Optional: checklist / SN002 wiring URL override</Label>
                <Input
                  value={fbrValidateURL}
                  onChange={(e) => setFbrValidateURL(e.target.value)}
                  placeholder="blank → sandbox https://gw.fbr.gov.pk/di_data/v1/di/postinvoicedata_sb"
                />
                <p className="text-[11px] text-muted-foreground">
                  Targets the endpoint used by the <span className="font-medium">Validate SN002 invoice</span> button only — the same
                  JSON body outbound sync validates first (defaults to the sandbox{' '}
                  <span className="font-mono">postinvoicedata_sb</span> path).
                </p>
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-muted-foreground">Optional: final invoice POST URL override</Label>
                <Input
                  value={fbrPostURL}
                  onChange={(e) => setFbrPostURL(e.target.value)}
                  placeholder="blank → sandbox postinvoicedata_sb or live …/di/postinvoicedata per toggle"
                />
                <p className="text-[11px] text-muted-foreground">
                  Where checkout / worker sync submits the invoice <span className="font-semibold">after</span> a successful validate
                  step — usually{' '}
                  <span className="font-mono">postinvoicedata_sb</span> (sandbox) or{' '}
                  <span className="font-mono">postinvoicedata</span> (production). Do not reuse a pure “validate” host path here
                  unless FBR directs you.
                </p>
              </div>
            </div>
          )}

          {authority === 'PRA' && (
            <>
              <div className="space-y-2">
                <Label>PNTN (if different from NTN, optional)</Label>
                <Input value={pntn} onChange={(e) => setPntn(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>POS registration number</Label>
                <Input value={posReg} onChange={(e) => setPosReg(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>SFD proxy URL</Label>
                <Input value={sfdUrl} onChange={(e) => setSfdUrl(e.target.value)} placeholder="http://localhost:16701" />
              </div>
            </>
          )}

          {(authority === 'FBR' || authority === 'PRA' || authority === 'PRA_EIMS' || authority === 'PRAL_POS' || authority === 'LOCAL_BRIDGE') && (
            <div className="space-y-2">
              <Label>
                {praEims
                  ? 'PRA Bearer token (required)'
                  : 'API token / access code (leave blank to keep stored secret)'}
              </Label>
              {praEims && (
                <p className="text-xs text-muted-foreground">
                  Sandbox uses PRA&apos;s published shared token. The production token is per-POS and
                  appears on the <strong>POS Details</strong> tab against your POS ID at
                  reg.pra.punjab.gov.pk.
                </p>
              )}
              <Input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                autoComplete="off"
                placeholder={data?.api_key_set ? '••••' + (data.api_key_masked || '') : 'Paste new token to save'}
              />
            </div>
          )}

          <div className="space-y-2 pt-2">
            {(productionTokenMissing || productionPosIdMissing) && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                <p className="font-semibold">
                  {praEims
                    ? 'PRA e-IMS is missing required credentials — saving will be blocked.'
                    : 'Production mode is missing required credentials — saving will be blocked.'}
                </p>
                <ul className="list-disc pl-4 mt-1 space-y-0.5">
                  {productionPosIdMissing && (
                    <li>
                      {praEims
                        ? 'POS ID is required. Use a test POS ID from PRA’s “Generate Test POS” screen for sandbox, or your registered POS ID for live.'
                        : 'POS ID is required for production POSTs.'}
                    </li>
                  )}
                  {productionTokenMissing && (
                    <li>
                      {praEims ? (
                        <>
                          Bearer token is required. Sandbox uses PRA’s published shared token; the live
                          token is per-POS, from the <strong>POS Details</strong> tab at
                          reg.pra.punjab.gov.pk.
                        </>
                      ) : (
                        <>
                          Bearer token is required. Paste the FBR-issued <strong>live</strong> token (sandbox tokens
                          will not authenticate against gw.fbr.gov.pk Live).
                        </>
                      )}
                    </li>
                  )}
                </ul>
                <p className="mt-1 text-muted-foreground">
                  Without these, the system would silently fall back to mock receipts in earlier builds. This is now
                  blocked for safety.
                </p>
              </div>
            )}
            {authorityNeedsToken && !isSandbox && data?.api_key_set && !apiKey.trim() && (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                {praEims ? (
                  <>
                    Using stored token. PRA sandbox and production use <strong>different</strong> tokens and
                    different POS IDs — paste the live token here when you switch, or every submission will be
                    rejected with <span className="font-mono">900901 Invalid Credentials</span>.
                  </>
                ) : (
                  <>
                    Using stored token. If you switched from sandbox to production, paste the FBR{' '}
                    <strong>live</strong> token here to overwrite — sandbox tokens always fail against
                    gw.fbr.gov.pk Live.
                  </>
                )}
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => saveMut.mutate()}
                disabled={saveMut.isPending || productionTokenMissing || productionPosIdMissing}
              >
                {saveMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save fiscal settings
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => testMut.mutate()}
                disabled={
                  testMut.isPending ||
                  authority === 'NONE' ||
                  (authority === 'PRAL_POS' && (!posId.trim() || (!posPostUrl.trim() && !isSandbox))) ||
                  (authority === 'PRAL_POS' && !isSandbox && !data?.api_key_set && !apiKey.trim()) ||
                  (authority === 'LOCAL_BRIDGE' && !posId.trim()) ||
                  (authority === 'LOCAL_BRIDGE' && !isSandbox && !data?.api_key_set && !apiKey.trim()) ||
                  // PRA needs both in either mode — the sandbox token and test
                  // POS ID are distinct values, not defaults.
                  (praEims && (!posId.trim() || (!data?.api_key_set && !apiKey.trim())))
                }
              >
                {testMut.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Test connection (live HTTP)
              </Button>
            </div>
            <p className="text-xs text-muted-foreground max-w-prose">
              Per-order fiscal sync status and PRA print state are in{' '}
              <strong>View Reports</strong> → <strong>Daily Sales</strong> → use <strong>Drill in</strong> on a date for orders, PRA, and receipts.
            </p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Test result</DialogTitle>
            <DialogDescription>
              {authority === 'LOCAL_BRIDGE' && isSandbox
                ? 'Sandbox mode — this page POSTs a test invoice directly to localhost:8524 (IMSFiscal on this machine) and shows the returned IRN and QR code. Run from the counter machine.'
                : authority === 'LOCAL_BRIDGE' && !isSandbox
                  ? 'Production mode — the server POSTs a test invoice to gw.fbr.gov.pk (port 443) and returns the IRN and QR. Same as PRAL POS production path.'
                  : authority === 'PRAL_POS'
                  ? 'Live POST to your PRAL / FBR ESP URL. Below we always show the exact outbound JSON body and the raw gateway response so you can diff against Postman. Passed means Code 100 and an IRN.'
                  : authority === 'FBR'
                    ? 'Minimal sandbox invoice through FBR DI; full receipt preview appears when the response includes printable mirror fields.'
                    : 'Connectivity check for the selected fiscal authority.'}
            </DialogDescription>
          </DialogHeader>
          {testResult && (
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                {testResult.error ? (
                  <Badge variant="destructive">Failed</Badge>
                ) : testResult.ok ? (
                  <Badge variant="success">Passed</Badge>
                ) : (
                  <Badge variant="warning">Incomplete</Badge>
                )}
                {testResult.authority ? (
                  <span className="text-xs text-muted-foreground font-mono">{testResult.authority}</span>
                ) : null}
              </div>
              {testResult.error ? (
                <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-destructive">Gateway rejection</p>
                  <p className="text-sm text-destructive mt-1 whitespace-pre-wrap break-words">{testResult.error}</p>
                  {(testResult.response_code || testResult.response_message) && (
                    <p className="text-xs text-muted-foreground mt-2 font-mono">
                      {testResult.response_code ? `Code ${testResult.response_code}` : ''}
                      {testResult.response_message ? ` — ${testResult.response_message}` : ''}
                    </p>
                  )}
                </div>
              ) : null}

              {(testResult.authority === 'PRAL_POS' || testResult.authority === 'LOCAL_BRIDGE') ? (
                <div className="rounded-lg border-2 border-primary/25 bg-muted/40 p-3 space-y-2">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wide text-foreground">
                        Outbound POST body (what Chaikhana sent)
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5 max-w-prose">
                        Exact HTTP POST JSON generated server-side for this test (same body as the live POST). Compare to Postman —{' '}
                        <span className="font-mono">items</span> key, PCT codes, totals.
                      </p>
                    </div>
                    {pralOutboundPreview ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="shrink-0"
                        onClick={() => {
                          const blob = new Blob([pralOutboundPreview], { type: 'application/json' })
                          const u = URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = u
                          a.download = 'chaikhana-pral-pos-request.json'
                          a.click()
                          URL.revokeObjectURL(u)
                        }}
                      >
                        <FileDown className="w-4 h-4 mr-1 inline" aria-hidden />
                        Download JSON
                      </Button>
                    ) : null}
                  </div>
                  {pralPreviewBuildErr ? (
                    <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm text-amber-950 dark:text-amber-100">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-900 dark:text-amber-200">
                        Could not build outbound preview
                      </p>
                      <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs">{pralPreviewBuildErr}</p>
                    </div>
                  ) : null}
                  {testResult.post_url ? (
                    <p className="text-[11px] text-muted-foreground break-all font-mono border-t pt-2">
                      <span className="font-semibold text-foreground">POST URL:</span> {testResult.post_url}
                    </p>
                  ) : null}
                  {pralOutboundPreview ? (
                    <>
                      <pre className="max-h-[min(28rem,55vh)] overflow-auto rounded-md border bg-background p-3 font-mono text-[11px] leading-relaxed whitespace-pre-wrap break-all">
                        {pralOutboundPreview}
                      </pre>
                      {pralOutboundPreview.endsWith('…') || pralOutboundPreview.endsWith('...') ? (
                        <p className="text-[10px] text-amber-700 dark:text-amber-400">
                          Preview may be truncated server-side; download JSON for the full buffer when available.
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="text-sm text-destructive">
                      No <span className="font-mono">request_preview</span> in the API response — rebuild and redeploy the backend so PRAL test
                      responses include the outbound JSON (field is always set on current servers). If you already deployed, hard-refresh the admin
                      page and run Test connection again.
                    </p>
                  )}
                </div>
              ) : null}

              {testResult.invoice_print && testResult.irn ? (
                <FiscalInvoiceDocument
                  invoice={testResult.invoice_print}
                  irn={testResult.irn}
                  qrPayload={testResult.qr_code_value}
                  logoUrl={receiptBranding.logoUrl}
                  logoWidthPercent={receiptBranding.logoWidthPercent}
                  orderNumber="TEST-1"
                />
              ) : (
                <>
                  {testResult.irn ? (
                    <div className="rounded-md border bg-muted/40 px-3 py-2">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                        IRN (printed on receipt)
                      </p>
                      <p className="text-sm font-mono break-all">{testResult.irn}</p>
                      {/* PRA answers Code 100 identically in both environments — the
                          *test* suffix on the invoice number is the ONLY tell. A tenant
                          that thinks it is live while posting to sandbox files nothing
                          with the authority and cannot otherwise notice. */}
                      {testResult.authority === 'PRA_EIMS' &&
                        (testResult.irn.toLowerCase().includes('*test*') ? (
                          <p className="mt-2 text-xs font-medium text-amber-600 dark:text-amber-500">
                            Sandbox invoice — the <span className="font-mono">*test*</span> suffix means PRA did{' '}
                            <strong>not</strong> file this for real.
                            {testResult.is_sandbox === false && (
                              <>
                                {' '}This authority is configured for <strong>production</strong>, so the saved
                                config still points at sandbox — did you press{' '}
                                <strong>Save fiscal settings</strong> after switching the toggle?
                              </>
                            )}
                          </p>
                        ) : (
                          <p className="mt-2 text-xs font-medium text-emerald-600 dark:text-emerald-500">
                            Live invoice number — no <span className="font-mono">*test*</span> suffix. This was filed
                            with PRA for real.
                          </p>
                        ))}
                    </div>
                  ) : null}
                  {(() => {
                    // F-FBR-N12: mirror the backend's ReceiptQRPayload rule, or this
                    // panel lies about what the printer emits. PRA encodes the bare
                    // invoice number; everyone else encodes their stored qr_code_value.
                    // This panel previously rendered qr_code_value for every authority
                    // while captioning it "the same IRN string your thermal receipt
                    // uses" — which for PRA was the verification URL, not the IRN.
                    const isPraTest = String(testResult.authority ?? '').toUpperCase().trim() === 'PRA_EIMS'
                    const qrPayload = isPraTest
                      ? String(testResult.irn ?? '').trim()
                      : String(testResult.qr_code_value ?? '').trim()
                    if (!qrPayload) return null
                    const verifyLink = String(testResult.qr_code_value ?? '').trim()
                    return (
                      <div className="flex flex-col items-center gap-2 p-4 border rounded-md bg-background">
                        <p className="text-xs text-muted-foreground text-center">
                          Verification QR — encodes exactly what your thermal receipt prints
                        </p>
                        <FiscalTestQrImg value={qrPayload} size={180} />
                        <p className="text-[10px] break-all font-mono text-foreground max-w-full text-center">{qrPayload}</p>
                        {isPraTest && verifyLink && verifyLink !== qrPayload ? (
                          <p className="text-[10px] break-all text-muted-foreground max-w-full text-center">
                            Look up online: {verifyLink}
                          </p>
                        ) : null}
                      </div>
                    )
                  })()}
                </>
              )}

              {testResult.raw ? (
                <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Gateway response (raw)</p>
                  <pre className="max-h-48 overflow-auto rounded-md border bg-background p-3 font-mono text-[11px] leading-snug whitespace-pre-wrap break-all">
                    {testResult.raw}
                  </pre>
                </div>
              ) : null}

              {testResult.authority !== 'PRAL_POS' && testResult.authority !== 'LOCAL_BRIDGE' && testResult.post_url ? (
                <p className="text-[11px] text-muted-foreground break-all font-mono">POST URL: {testResult.post_url}</p>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={sn002DialogOpen} onOpenChange={setSn002DialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>FBR DI sandbox — SN002 validate</DialogTitle>
            <DialogDescription>
              Validates the same JSON FBR receives (your saved seller fields and SN002 line template). After{' '}
              <strong>OK</strong>, download a printable test receipt built from that request plus FBR’s response
              (invoice number / QR). Use <strong>Test connection</strong> for a full round-trip post with live slip
              preview.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            {sn002Mut.isPending && !sn002Result && (
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Calling validate endpoint…
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!data?.api_key_set || sn002Mut.isPending}
                onClick={() => {
                  setSn002Result(null)
                  sn002Mut.mutate()
                }}
              >
                {sn002Mut.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                Run again
              </Button>
              {sn002Result?.request_preview ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const blob = new Blob([sn002Result.request_preview || ''], { type: 'application/json' })
                    const u = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = u
                    a.download = 'chaikhana-fbr-sn002-request.json'
                    a.click()
                    URL.revokeObjectURL(u)
                  }}
                >
                  Download request JSON
                </Button>
              ) : null}
              {sn002Result?.response_body ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const blob = new Blob([sn002Result.response_body || ''], { type: 'application/json' })
                    const u = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = u
                    a.download = 'chaikhana-fbr-sn002-response.json'
                    a.click()
                    URL.revokeObjectURL(u)
                  }}
                >
                  Download response JSON
                </Button>
              ) : null}
              {sn002Result?.ok && sn002Result.request_preview ? (
                <Button
                  type="button"
                  size="sm"
                  className="gap-2"
                  disabled={sn002ReceiptBusy}
                  onClick={async () => {
                    setSn002ReceiptBusy(true)
                    try {
                      const ok = await downloadSn002TestReceipt(
                        sn002Result,
                        receiptBranding.logoUrl,
                        receiptBranding.logoWidthPercent,
                      )
                      if (!ok) {
                        toast({
                          title: 'Could not build test receipt',
                          description:
                            'The FBR response must include an invoice number or qrCode, and the request JSON must parse.',
                          variant: 'destructive',
                        })
                      } else {
                        toast({
                          title: 'Test receipt downloaded',
                          description: 'Open the HTML file and print or save as PDF for your demo.',
                        })
                      }
                    } finally {
                      setSn002ReceiptBusy(false)
                    }
                  }}
                >
                  {sn002ReceiptBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                  Download test receipt
                </Button>
              ) : null}
            </div>
            {sn002Result && (
              <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-2">
                <p className="font-mono text-xs">
                  {sn002Result.ok ? 'OK' : 'Check response'} — HTTP {sn002Result.http_status}
                </p>
                {sn002Result.validation_parent_status ? (
                  <p className="text-xs">
                    Validation parent <span className="font-mono">{sn002Result.validation_parent_status}</span>
                  </p>
                ) : null}
                {sn002Result.response_invoice_number ? (
                  <p className="text-xs break-all">
                    Invoice number (if present):{' '}
                    <span className="font-mono">{sn002Result.response_invoice_number}</span>
                  </p>
                ) : null}
                {sn002Result.seller_registration && (
                  <p className="text-[11px] text-muted-foreground">
                    Seller id: <span className="font-mono">{sn002Result.seller_registration.normalized}</span> from{' '}
                    <span className="font-mono uppercase">{sn002Result.seller_registration.source}</span> (raw:{' '}
                    <span className="font-mono break-all">{sn002Result.seller_registration.raw}</span>)
                  </p>
                )}
                {sn002Result.validate_url && (
                  <p className="text-[11px] break-all text-muted-foreground">{sn002Result.validate_url}</p>
                )}
                {sn002Result.error && <p className="text-xs text-destructive">{sn002Result.error}</p>}
                <details className="text-xs">
                  <summary className="cursor-pointer font-medium text-muted-foreground">Technical details</summary>
                  {sn002Result.request_preview ? (
                    <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words max-h-40 overflow-auto p-2 bg-background rounded border">
                      {sn002Result.request_preview}
                    </pre>
                  ) : null}
                  {sn002Result.response_body ? (
                    <pre className="mt-2 text-[11px] whitespace-pre-wrap break-words max-h-40 overflow-auto p-2 bg-background rounded border">
                      {sn002Result.response_body}
                    </pre>
                  ) : null}
                </details>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
